import { useTranslation } from "react-i18next";
import type { JSX } from "react";
import { memo, useCallback } from "react";

import type { IFieldProps } from "../field";

import { usePhoneCodes } from "@api/dropdowns";
import type { IPhoneNumber } from "@components/formControls/phone";
import { Phone } from "@components/formControls/phone";
import { Field } from "@/components/fastFields/field/field";
import { useReadOnly } from "@/hooks";
import { checkFormikEqual } from "@utils";

export type IProps<T> = {
  disabled?: boolean;
  placeholder?: string;
  readonly?: boolean;
} & Required<Pick<IFieldProps<T>, "formik" | "name">> &
  Partial<Pick<IFieldProps<T>, "icon" | "title" | "isRequired">>;

export const FastPhone = memo(
  function FastPhone<T>(props: IProps<T>) {
    const readonly = useReadOnly(props.readonly);
    const enabled = !readonly;
    const { data, isSuccess, isLoading } = usePhoneCodes({
      enabled,
    });

    const { t } = useTranslation("form");
    const title = props.title || "phone";
    const placeholder = props.placeholder || t(title);
    const name = props.name;
    const { values, setFieldValue, errors, touched, handleBlur } = props.formik;
    const value = values[name] as IPhoneNumber;

    const countries = isSuccess ? data.data : getPhoneCodes();

    const handleChange = useCallback(
      (val: IPhoneNumber) => {
        setFieldValue(name.toString(), val);
      },
      [name, setFieldValue],
    );

    return (
      <Field
        name={name}
        title={title}
        formik={props.formik}
        icon={props.icon || "PhoneCallingRounded"}
        isRequired={props.isRequired && !readonly}
      >
        <Phone
          countries={countries}
          placeholder={placeholder}
          id={name.toString()}
          onChangeValue={handleChange}
          onBlur={handleBlur}
          value={value}
          disabled={props.disabled || (isLoading && !readonly)}
          validation={touched[name] && errors[name] ? "error" : undefined}
          readOnly={readonly}
        />
      </Field>
    );
  },
  (prevProps, nextProps) => checkFormikEqual(nextProps, prevProps),
) as unknown as <T>(props: IProps<T>) => JSX.Element;

const getPhoneCodes = () => {
  if (import.meta.env.VITE_LANGUAGE === "fa") {
    return [
      {
        key: "+98",
        flag: "https://cdn.kcak11.com/CountryFlags/countries/ir.svg",
        value: "ایران",
      },
    ];
  }
  return [
    {
      key: "+964",
      flag: "https://cdn.kcak11.com/CountryFlags/countries/iq.svg",
      value: "عراق",
    },
  ];
};
